import { useState, useRef, useEffect } from 'react';

/**
 * GroupResidentsCell — shows a resident count badge with a popover listing
 * the residents in a group. Follows the same hover/click popover pattern
 * used by DeviceCell.
 *
 * @param {{ residents: Array<{ resident_id: number, resident_name: string, area_name: string, area_type_name: string }> }} props
 */
const GroupResidentsCell = ({ residents }) => {
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState({});
  const popoverRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

    const computePosition = () => {
        if (popoverRef.current) {
            const rect = popoverRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 200) {
                setPopoverStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 4}px`, left: `${rect.left}px`, top: 'auto', right: 'auto', margin: 0, zIndex: 9999, transform: 'none' });
            } else {
                setPopoverStyle({ position: 'fixed', top: `${rect.bottom + 4}px`, left: `${rect.left}px`, bottom: 'auto', right: 'auto', margin: 0, zIndex: 9999, transform: 'none' });
            }
        }
    };

    const handleOpen = (nextOpen) => {
        if (nextOpen) computePosition();
        setOpen(nextOpen);
    };

  if (!residents || residents.length === 0) {
    return <span className="rp-cell-text--muted">None</span>;
  }

  const count = residents.length;

  return (
    <div className="rp-group-residents-cell" ref={popoverRef}>
      <button
        type="button"
        onClick={() => handleOpen(!open)}
        onMouseEnter={() => handleOpen(true)}
        onMouseLeave={() => handleOpen(false)}
        className="rp-group-residents-badge"
        aria-label={`${count} resident${count !== 1 ? 's' : ''} in group`}
      >
        <i className="fe fe-users" aria-hidden="true" />
        {count}
      </button>

      {open && (
        <div className="rp-group-residents-popover" style={popoverStyle} role="tooltip">
          <p className="rp-group-residents-popover__title">
            Group Residents
          </p>
          <ul className="rp-group-residents-popover__list">
            {residents.map((r) => (
              <li key={r.resident_id} className="rp-group-residents-popover__item">
                <span className="rp-group-residents-popover__name">{r.resident_name}</span>
                <span className="rp-group-residents-popover__area">
                  {r.area_name}
                  {r.area_type_name && (
                    <span className="rp-area-type-tag rp-area-type-tag--sm">{r.area_type_name}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default GroupResidentsCell;
